/**
 * Charge-object approvers — Supabase CRUD for the per-charge-object approver
 * assignment that drives charge-object approval routing (Time Approval Routing,
 * Part 2). Production-only against Supabase; defensive empty-on-error so the app
 * keeps working before migration 00379 is applied.
 *
 * The assignment lives in its own table (charge_object_approvers), NOT as a
 * column on sap_charge_objects: that row is rebuilt on every re-pull and would
 * clobber the approver. See docs/TIME_APPROVAL_ROUTING.md.
 */
import { supabase } from '@/lib/supabase'
import { generateWithClaude } from '@/services/aiService'
import { type SapObjectType, sapChargeObjectId } from '../lib/sapChargeObjects'
import {
  type ApproverSource, type ChargeObjectApprover, type DbChargeObjectApprover,
  mapDbApprover, buildApproverMapByKey,
} from '../lib/chargeObjectApprovers'
import { parseApproverPairs, type ParsedApproverPair } from '../lib/approverImportResolution'

export type { ApproverSource, ChargeObjectApprover } from '../lib/chargeObjectApprovers'

type DbApprover = DbChargeObjectApprover

const COLS = 'org_id, id, sap_object_type, object_key, approver_user_id, approver_name, source, assigned_by, assigned_at, updated_at'

const toApp = mapDbApprover

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

const SELECT_PAGE = 1000

/** All charge-object approvers for an org (paged so a large catalog isn't truncated). */
export async function listChargeObjectApprovers(orgId: string): Promise<ChargeObjectApprover[]> {
  try {
    const all: DbApprover[] = []
    for (let from = 0; ; from += SELECT_PAGE) {
      const { data, error } = await supabase
        .from('charge_object_approvers')
        .select(COLS)
        .eq('org_id', orgId)
        .order('id', { ascending: true })
        .range(from, from + SELECT_PAGE - 1)
      if (error) { if (from === 0) console.warn('[chargeObjectApproverService] list', error); break }
      const rows = (data ?? []) as DbApprover[]
      all.push(...rows)
      if (rows.length < SELECT_PAGE) break
    }
    return all.map(toApp)
  } catch (e) { console.warn('[chargeObjectApproverService] list threw', e); return [] }
}

/**
 * Map from charge-object key -> assignment, the shape the routing engine needs.
 * Keyed by object_key (what a timesheet entry's charge code resolves to via
 * TsChargeCode.wbs / SapChargeObject.objectKey).
 */
export async function getApproverMapByKey(orgId: string): Promise<Map<string, ChargeObjectApprover>> {
  return buildApproverMapByKey(await listChargeObjectApprovers(orgId))
}

export interface UpsertApproverInput {
  orgId: string
  sapObjectType: SapObjectType
  objectKey: string
  approverUserId: string
  approverName?: string | null
  source?: ApproverSource
  assignedBy?: string | null
}

function toRow(input: UpsertApproverInput, now: string): DbApprover {
  return {
    org_id: input.orgId,
    id: sapChargeObjectId(input.sapObjectType, input.objectKey),
    sap_object_type: input.sapObjectType,
    object_key: input.objectKey,
    approver_user_id: input.approverUserId,
    approver_name: input.approverName ?? null,
    source: input.source ?? 'manual',
    assigned_by: input.assignedBy ?? null,
    assigned_at: now,
    updated_at: now,
  }
}

export async function upsertChargeObjectApprover(input: UpsertApproverInput): Promise<ChargeObjectApprover | null> {
  const row = toRow(input, new Date().toISOString())
  const { error } = await supabase.from('charge_object_approvers').upsert(row, { onConflict: 'org_id,id' })
  if (error) { console.warn('[chargeObjectApproverService] upsert', error); return null }
  return toApp(row)
}

/**
 * Bulk-upsert assignments in chunks (one write per ~500 rows). Used by the
 * multi-select bulk-assign, the PM-team pull (PR2), and the AI import (PR3).
 * Returns how many rows were written.
 */
export async function bulkUpsertChargeObjectApprovers(inputs: UpsertApproverInput[]): Promise<number> {
  if (inputs.length === 0) return 0
  const now = new Date().toISOString()
  const rows = inputs.map((i) => toRow(i, now))
  let ok = 0
  for (const part of chunk(rows, 500)) {
    const { error } = await supabase.from('charge_object_approvers').upsert(part, { onConflict: 'org_id,id' })
    if (error) console.warn('[chargeObjectApproverService] bulkUpsert chunk', error)
    else ok += part.length
  }
  return ok
}

export async function deleteChargeObjectApprover(orgId: string, id: string): Promise<boolean> {
  const { error } = await supabase.from('charge_object_approvers').delete().eq('org_id', orgId).eq('id', id)
  if (error) { console.warn('[chargeObjectApproverService] delete', error); return false }
  return true
}

/** Bulk-clear assignments by synthetic id. Returns rows requested for deletion. */
export async function bulkDeleteChargeObjectApprovers(orgId: string, ids: string[]): Promise<number> {
  if (ids.length === 0) return 0
  let n = 0
  for (const part of chunk(ids, 500)) {
    const { error } = await supabase.from('charge_object_approvers').delete().eq('org_id', orgId).in('id', part)
    if (error) console.warn('[chargeObjectApproverService] bulkDelete chunk', error)
    else n += part.length
  }
  return n
}

// ── Approver candidate picker ────────────────────────────────────────────────

export interface ApproverCandidate {
  id: string
  name: string
  title: string | null
}

/**
 * Users in the org who can be picked as a charge-object approver. Scoped to the
 * passed orgId (the timesheet module sources orgId from the app store, not the
 * localStorage org context that listUsersForPicker uses).
 */
export async function listApproverCandidates(orgId: string): Promise<ApproverCandidate[]> {
  try {
    // `users.organization_id` is a UUID column; the demo's org id is the TEXT
    // literal 'org_demo' — filtering with it 400s (22P02) on every consumer
    // (Settings delegations, Approvers tab, PTO coverage). In demo (any
    // non-uuid org id) return the unscoped roster instead: the demo universe
    // IS the candidate pool, and an empty pool would break the pickers.
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orgId)
    let q = supabase.from('users').select('id, name, title').order('name').limit(500)
    if (isUuid) q = q.eq('organization_id', orgId)
    const { data, error } = await q
    if (error) { console.warn('[chargeObjectApproverService] candidates', error); return [] }
    return ((data ?? []) as Array<{ id: string; name: string | null; title: string | null }>)
      .map((u) => ({ id: u.id, name: u.name ?? u.id, title: u.title }))
  } catch (e) { console.warn('[chargeObjectApproverService] candidates threw', e); return [] }
}

// ── AI import: free text -> structured pairs (Part 2, AI import) ──────────────

/**
 * Normalize a pasted free-text "WBS -> approver" mapping into structured
 * { wbsRef, approverRef } pairs. The AI ONLY normalizes the text; the actual
 * mapping of refs to real charge objects + users is deterministic (see
 * lib/approverImportResolution.resolveApproverImport). Uses generateWithClaude
 * with a clean single-purpose system prompt (no tools / no portfolio persona),
 * mirroring planSapPull. Returns [] if the engine is unreachable / returns
 * nothing parseable.
 */
export async function parseApproverMappingText(text: string): Promise<ParsedApproverPair[]> {
  const trimmed = text.trim()
  if (!trimmed) return []
  const system = [
    'You convert a pasted free-text mapping of WBS / charge objects to their approver into structured JSON.',
    'Each line or entry pairs a charge object reference (a WBS element id / code, e.g. "E-1234.01" or "U-1RY.001") with an approver (a person name OR a user id like "user_007").',
    'Return ONLY a JSON array, no prose and no markdown fences:',
    '[{"wbsRef": string, "approverRef": string}]',
    'wbsRef = the charge object reference exactly as written (do not invent or reformat the id). approverRef = the approver name or user id exactly as written.',
    'One entry per pairing. If a line lists multiple WBS for one approver, emit one entry per WBS. Ignore headers, blank lines, and commentary. Do not fabricate pairings that are not present in the text.',
  ].join('\n')
  try {
    const raw = await generateWithClaude(system, `Text to convert:\n${trimmed}\n\nReturn the JSON array now.`, 1500)
    return parseApproverPairs(raw ?? '')
  } catch (e) {
    console.warn('[chargeObjectApproverService] parseApproverMappingText failed', e)
    return []
  }
}
